import {
  ImageIntentContext,
  isSaveBlockingEnabled,
  reportImageIntent,
} from '../../../../utils/imageIntentReporter';
import { IMAGE_INTENT_ATTRIBUTE } from './ImageIntentTouchStyle';

// How long a still finger must stay down to count as a long-press.
//
// Measured in Mobile Safari (iOS 18): when its own long-press save sheet is
// allowed to open, Safari cancels the page's touch (touchcancel) at the moment
// the sheet appears — 734–999 ms after touchstart. With the sheet suppressed
// (ImageIntentTouchStyle) the touch ends normally. Either way this timer fires
// first, so the attempt is logged whether or not the sheet is blocked.
export const LONG_PRESS_MS = 600;

// A touchcancel after the finger has been still this long is the OS claiming
// the long-press for itself (save sheet, drag lift), so it is logged as one. An
// earlier cancel — a scroll the browser took over, a system interruption — is
// not a long-press.
export const OS_LONG_PRESS_CLAIM_MS = 450;

// How far a finger may wander and still be holding still. A real finger is
// never perfectly still: Mobile Safari and Chrome Android both send touchmove
// during a long-press. This matches UIKit's long-press allowableMovement
// (10 pt) and exceeds Android's 8 dp touch slop, so a wander the OS still
// treats as a hold is a hold here too; a scroll or swipe passes it in its
// first frame. Measured in the iOS 18 simulator: Safari still lifted the image
// after 5.7 pt of wander and gave up at 11.3 pt.
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

export interface TouchPoint {
  x: number;
  y: number;
}

// The first finger's position, and how many fingers are down, from a touch
// event (React's or the DOM's).
export const touchPointOf = (event: {
  touches: ArrayLike<{ clientX: number; clientY: number }>;
}): TouchPoint | undefined => {
  const touch = event.touches[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : undefined;
};

// Which kind of pointer opened a context menu. Chromium dispatches contextmenu
// as a PointerEvent, so a finger's long-press says 'touch'; a browser that
// sends a plain MouseEvent says nothing.
export const pointerTypeOf = (event: Event): string | undefined =>
  'pointerType' in event && typeof event.pointerType === 'string'
    ? event.pointerType
    : undefined;

// Whether this browser's long-press save path is the iOS/iPadOS callout sheet,
// which only CSS can stop (true on WebKit; false on Android Chrome, Firefox
// and Samsung Internet, and in jsdom). On WebKit a long-press is blocked iff
// the studio's switch applies ImageIntentTouchStyle. Everywhere else its save
// paths are the contextmenu that follows, which the page always cancels, and,
// in Chrome on an Android tablet, a drag out to another app, which only the
// switch cancels (see longPressSettledByWhatFollows). Memoised: the answer
// never changes within a page.
let touchCalloutSupported: boolean | undefined;

export const supportsTouchCallout = (): boolean => {
  if (touchCalloutSupported === undefined) {
    touchCalloutSupported =
      typeof CSS !== 'undefined' &&
      typeof CSS.supports === 'function' &&
      CSS.supports('-webkit-touch-callout', 'none');
  }
  return touchCalloutSupported;
};

// Test-only helper: forget the memoised probe so a test can stub window.CSS.
export const __resetSupportsTouchCalloutForTests = (): void => {
  touchCalloutSupported = undefined;
};

// Whether a long-press that no drag settled (the timer, a late touchcancel,
// or a counted hold that ends) was blocked: on WebKit the sheet is suppressed
// iff the switch is on; elsewhere the contextmenu is always cancelled.
const longPressBlocked = (): boolean =>
  isSaveBlockingEnabled() || !supportsTouchCallout();

// Whether the timer must leave a long-press's blocked state to what the touch
// does next. Chrome on an Android tablet fires dragstart (a drag out to
// another app) and then contextmenu at the OS long-press delay, which a
// lengthened "Touch & hold delay" (1000 or 1500 ms) puts after the 600 ms
// timer. With the switch off that drag is allowed, so the touch is not blocked
// although its menu is; the timer cannot know yet whether a drag will follow
// (a phone opens only the menu). On WebKit, or with the switch on, every path
// a long-press has is decided already, so the timer reports at once.
const longPressSettledByWhatFollows = (): boolean =>
  !isSaveBlockingEnabled() && !supportsTouchCallout();

interface Cancelable {
  preventDefault: () => void;
}

export interface ImageIntentGestureTracker {
  contextMenu: (
    event: Cancelable,
    context: ImageIntentContext,
    pointerType?: string,
  ) => void;
  dragStart: (event: Cancelable, context: ImageIntentContext) => void;
  touchStart: (
    context: ImageIntentContext,
    point?: TouchPoint,
    touchCount?: number,
  ) => void;
  touchMove: (point?: TouchPoint, touchCount?: number) => void;
  touchEnd: () => void;
  touchCancel: (context: ImageIntentContext) => void;
  dispose: () => void;
}

// Logs every save gesture on a preview image, blocks the ones that save, and
// reports with each attempt whether its save path was blocked:
//   - contextmenu: always blocked (preview images blocked right-click before
//     this feature, so the studio's save-blocking switch does not touch it).
//     From a finger it is that touch's long-press, and is logged as one rather
//     than as a right-click: Android Chrome opens its image menu while the
//     finger is still down, and Chromium on a Windows touchscreen opens it
//     only once the finger lifts (after touchend), with pointerType 'touch'.
//     Reported blocked either way.
//   - dragstart: blocked when the studio's save blocking is on, for every
//     pointer (finger, mouse or trackpad), and reported blocked iff it is.
//     No parent-facing screen uses drag-and-drop, so nothing legitimate
//     breaks. Mobile Safari fires dragstart while the touch is still down, so
//     an active touch marks a finger drag (drag to Photos/Files) even on an
//     iPad whose primary pointer is a trackpad. A finger can lift an image
//     only after holding it, and Safari lifts it 0.4–1 s into any still hold,
//     before its save sheet opens, so a finger drag is logged as that touch's
//     long-press: one label for one gesture, whichever of the lift and the
//     timer comes first (which depends on the studio's blocking switch). A
//     drag with no active touch (mouse, trackpad) is logged as a drag.
//   - long-press: logged here, whichever of the timer, a finger dragstart, a
//     late touchcancel or a finger's contextmenu sees it first; the iOS save
//     sheet itself can only be stopped by CSS (ImageIntentTouchStyle). The
//     timer and a late touchcancel report it blocked per longPressBlocked
//     (see supportsTouchCallout), except that where a drag could still
//     follow (longPressSettledByWhatFollows) the timer only counts the
//     attempt: a finger dragstart or contextmenu in the same touch reports
//     it with its own outcome, and otherwise it is reported per
//     longPressBlocked when the touch ends. A finger that wanders less than
//     LONG_PRESS_MOVE_TOLERANCE_PX is still holding; a second finger (a
//     pinch) is never a long-press. The touch handlers never preventDefault,
//     so scrolling, pinch-zoom and carousel swipes are untouched.
// One touch is one attempt: once it has reported, it reports nothing else.
// Mobile Safari fires dragstart during a long-press, so otherwise one iOS
// long-press would be logged twice.
// Blocking never hides an attempt: each handler reports before it cancels, and
// what a gesture blocks does not depend on whether it was reported.
//
// Measured in Mobile Safari (iOS 18) — what each gesture sends:
//   long-press, sheet allowed   touchstart, dragstart +525, touchcancel +1035
//   long-press, sheet blocked   touchstart, dragstart +652, touchend +1274
//   hold, then drag (allowed)   touchstart, dragstart +418, touchcancel +762
//   wandering 1-2 pt hold       touchmoves, then the same lift and sheet
//
// Copy is deliberately not tracked. A browser sends the copy event to the
// element where the text selection starts, and a selection cannot start inside
// an <img> (most previews also sit in user-select: none containers, and
// ImageIntentTouchStyle turns selection off on touch), so a listener here would
// never fire. The real copy actions are already counted: right-click -> Copy
// Image is blocked and logged as contextmenu; the iOS sheet's "Copy" and
// Android's "Copy image" are logged as longpress.
export const createImageIntentGestureTracker =
  (): ImageIntentGestureTracker => {
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let touchStartedAt: number | null = null;
    let touchOrigin: TouchPoint | null = null;
    let touchActive = false;
    let touchReported = false;
    // A long-press the timer counted whose blocked state the rest of the
    // touch decides (longPressSettledByWhatFollows): its context, until then.
    let countedLongPress: ImageIntentContext | null = null;

    const clearLongPress = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const reportLongPress = (context: ImageIntentContext, blocked: boolean) => {
      countedLongPress = null;
      if (touchReported) return;
      touchReported = true;
      reportImageIntent('longpress', context, { blocked });
    };

    // Reports a counted long-press that nothing in its touch settled.
    const reportCountedLongPress = () => {
      if (countedLongPress)
        reportLongPress(countedLongPress, longPressBlocked());
    };

    return {
      contextMenu: (event, context, pointerType) => {
        if (touchActive || pointerType === 'touch') {
          clearLongPress();
          reportLongPress(context, true);
          // A finger's menu after touchend is the last event of the touch
          // that just ended: reported only if that touch was not. Whatever
          // comes next is a new attempt, even from a browser that sends the
          // page no touch events (so no touchstart to start one).
          if (!touchActive) touchReported = false;
        } else {
          reportImageIntent('contextmenu', context, { blocked: true });
        }
        event.preventDefault();
      },
      dragStart: (event, context) => {
        const blocked = isSaveBlockingEnabled();
        if (!touchActive) {
          reportImageIntent('dragstart', context, { blocked });
        } else {
          clearLongPress();
          reportLongPress(context, blocked);
        }
        if (blocked) {
          event.preventDefault();
        }
      },
      touchStart: (context, point, touchCount = 1) => {
        // A second finger: a pinch, not a long-press. The touch stays active
        // (a drag or menu during it is still a finger's), but nothing more is
        // timed.
        if (touchActive && touchCount > 1) {
          clearLongPress();
          return;
        }
        clearLongPress();
        reportCountedLongPress();
        touchActive = true;
        touchOrigin = point ?? null;
        touchReported = false;
        touchStartedAt = Date.now();
        // The browser may deliver the first touchstart with both fingers
        // already down. Keep it active, but do not time a pinch as a hold.
        if (touchCount > 1) return;
        longPressTimer = setTimeout(() => {
          longPressTimer = null;
          if (longPressSettledByWhatFollows()) {
            if (!touchReported) countedLongPress = context;
          } else {
            reportLongPress(context, longPressBlocked());
          }
        }, LONG_PRESS_MS);
      },
      touchMove: (point, touchCount = 1) => {
        if (!longPressTimer) return;
        if (touchCount > 1 || !point || !touchOrigin) {
          clearLongPress();
          return;
        }
        const dx = point.x - touchOrigin.x;
        const dy = point.y - touchOrigin.y;
        if (dx * dx + dy * dy > LONG_PRESS_MOVE_TOLERANCE_PX ** 2) {
          clearLongPress();
        }
      },
      touchEnd: () => {
        touchActive = false;
        clearLongPress();
        reportCountedLongPress();
      },
      touchCancel: (context) => {
        const stillPending = longPressTimer !== null;
        const heldMs =
          touchStartedAt === null ? 0 : Date.now() - touchStartedAt;
        touchActive = false;
        clearLongPress();
        if (stillPending && heldMs >= OS_LONG_PRESS_CLAIM_MS) {
          reportLongPress(context, longPressBlocked());
        }
        reportCountedLongPress();
      },
      // A touch still short of the timer is dropped; a long-press the timer
      // already counted is reported, as the attempt it was.
      dispose: () => {
        touchActive = false;
        clearLongPress();
        reportCountedLongPress();
      },
    };
  };

// The same logging and blocking for a preview image rendered outside React
// (the SweetAlert confirm dialogs), as native listeners. Returns a cleanup.
export const attachImageIntentListeners = (
  element: Element,
  context: ImageIntentContext,
): (() => void) => {
  const tracker = createImageIntentGestureTracker();
  element.setAttribute(IMAGE_INTENT_ATTRIBUTE, '');

  const listeners: Array<[string, (event: Event) => void]> = [
    [
      'contextmenu',
      (event) => tracker.contextMenu(event, context, pointerTypeOf(event)),
    ],
    ['dragstart', (event) => tracker.dragStart(event, context)],
    [
      'touchstart',
      (event) => {
        const touchEvent = event as TouchEvent;
        tracker.touchStart(
          context,
          touchPointOf(touchEvent),
          touchEvent.touches.length,
        );
      },
    ],
    [
      'touchmove',
      (event) => {
        const touchEvent = event as TouchEvent;
        tracker.touchMove(touchPointOf(touchEvent), touchEvent.touches.length);
      },
    ],
    ['touchend', () => tracker.touchEnd()],
    ['touchcancel', () => tracker.touchCancel(context)],
  ];
  listeners.forEach(([type, listener]) =>
    element.addEventListener(type, listener, {
      passive: type !== 'contextmenu' && type !== 'dragstart',
    }),
  );

  return () => {
    listeners.forEach(([type, listener]) =>
      element.removeEventListener(type, listener),
    );
    tracker.dispose();
  };
};
